<template>
    <div class="s3-select form-control" :class="{multiple: multiple, focused: focused}" @click="toggleDropdown()">
        <div class="s3-input" ref="select" tabindex="0" @keydown.down="toggleDropdown()" @focus="focused = true" @blur="focused = false">
            <div class="placeholder" v-if="!values.length">
                {{ placeholder }}
            </div>
            <div v-for="value in values" class="s3-value">
                {{ pullValue(value, labelKey) }}
                <a href @click.prevent.stop="remove(value)" v-show="allowClear || multiple"></a>
            </div>
            <i v-if="busy" class="fas fa-circle-notch fa-spin" style="margin-left: 10px;"></i>
            <input :disabled="disabled" v-if="multiple" type="text" ref="multipleSearch" @input="search" v-model="q" class="s3-multiple-search" @keydown.esc="toggleDropdown()" @keydown.up="focusUp()" @keydown.down.stop="focusDown()" @keydown.delete="removeItem" @keydown.enter.prevent="select(options[focusedOption])">
        </div>
        <div ref="dropdown" class="s3-dropdown" v-show="dropdown" @click.stop :style="position">
            <input :disabled="disabled" v-if="!multiple" type="text" ref="search" @input="search" v-model="q" class="s3-search" @keydown.esc="toggleDropdown()" @keydown.up="focusUp()" @keydown.down="focusDown()" @keydown.enter.prevent="select(options[focusedOption])">
            <div ref="options" class="s3-dropdown-options">
                <div ref="optionValues" class="option-values">
                    <a href ref="option" @click.prevent="select(value)" :disabled="isDisabled(value)" :class="{focused: focusedOption == index}" class="s3-option" @mouseenter="focusedOption = index" v-for="(value, index) in options">
                        {{ pullValue(value, labelKey) }}
                    </a>
                    <span class="s3-option" v-if="!busy && options.length === 0">No Results</span>
                    <a href @click.prevent="load(nextPage)" v-show="nextPage">Load More</a>
                </div>
            </div>
        </div>
    </div>
</template>
<style>
    .s3-select {
        position: relative;
        z-index: 1;
    }

    .s3-select.focused {
        border-color: #93a1bb;
    }

    .s3-select a {
        color: #999;
        text-decoration: none;
    }

    .s3-multiple-search {
        border: 0;
        outline: none;
    }

    .s3-search {
        width: 100%;
        outline: none;
    }

    .s3-value a:after {
        content: 'x';
    }

    .multiple .s3-value {
        display: inline;
        line-height: 23px;
        border: 1px solid #eee;
        background: #555;
        color: white;
        padding: 3px 7px;
    }

    .s3-input {
        width: 100%;
        height: 100%;
        outline: none;
        display: inline-block;
    }

    .s3-input a:hover {
        color: #000;
    }

    .s3-dropdown a[disabled] {
        background: #ccc;
        padding: 4px;
    }

    a.s3-option.focused {
        background: #41b883;
        color: white;
        text-decoration: none;
    }

    .s3-dropdown {
        background: white;
        min-width: 120px;
        box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
        z-index: 100000;
        top: 33px;
        position: absolute;
        left: 0;
        width: 100%;
    }

    .s3-dropdown-options {
        max-height: 200px;
        overflow: auto;
    }

    .s3-option {
        display: block;
        padding: 4px;
    }

    .s3-option:hover {
        text-decoration: none;
    }
    
    .s3-option:visited {
        color: #333;
    }
</style>
<script>

import axios from 'axios';
// TODO: Hide on scroll
export default {
    props: {
        placeholder: null,
        value: null,
        multiple: {
            type: Boolean,
            default: false
        },
        dataUrl: {
            type: String,
            required: true
        },
        allowClear: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        valueKey: {
            type: String,
            default: 'name',
        },
        label: {
            type: String,
            required: false
        },
    },
    data() {
        return {
            key: 'id',
            q: '',
            dropdown: false,
            preventClosing: false,
            nextPage: null,
            busy: false,
            focusedOption: 0,
            focused: false,
            options: [],
            values: []
        };
    },
    computed: {
        labelKey() {
            return this.label || this.valueKey;
        }    
    },
    methods: {
        focusDown() {
            if (!this.dropdown) {
                this.toggleDropdown(true);
                return;
            }
            if (!this.busy && this.focusedOption == this.options.length - 1) {
                this.load(this.nextPage);
            }
            this.focusedOption = Math.min(this.focusedOption + 1, Math.max(0, this.options.length - 1));
            if (!this.$refs.option) {
                return;
            }
            let opt = this.$refs.option[this.focusedOption];
            if (!opt) {
                return;
            }
            let viewportBottom = this.$refs.options.scrollTop + this.$refs.options.offsetHeight;
            if (opt.offsetTop + opt.offsetHeight > viewportBottom) {
                this.$refs.options.scrollTop = opt.offsetTop + opt.offsetHeight - this.$refs.options.offsetHeight;
            }
        },
        focusUp() {
            this.focusedOption = Math.max(this.focusedOption - 1, 0);
            if (!this.$refs.option) {
                return;
            }
            let opt = this.$refs.option[this.focusedOption];
            if (!opt) {
                return;
            }
            let viewportTop = this.$refs.options.scrollTop;
            if (opt.offsetTop <= viewportTop) {
                this.$refs.options.scrollTop = opt.offsetTop - opt.offsetHeight;
            }
        },
        position() {
            let el = this.$el.getBoundingClientRect();
            return {
                left: el.left + window.scrollX,
                top: el.top + window.scrollY
            };
        },
        toggleDropdown(state = null) {
            if (this.disabled) {
                return;
            }
            this.dropdown = (state === null) ? !this.dropdown : state;
            this.preventClosing = true;
            this.$nextTick(() => {
                this.preventClosing = false;
                let el = this.$refs[this.multiple ? 'multipleSearch' : (this.dropdown ? 'search' : 'select')];
                el.focus();
            });
        },
        removeItem(e) {
            if (this.q != '') {
                return;
            }
            if (this.values.length) {
                this.q = this.pullValue(this.values.pop(), this.valueKey);
                this.search();
                e.preventDefault();
            }
        },
        search() {
            this.load();
        },
        load(url) {
            if (this.busy) {
                return;
            }
            const newSearch = url === undefined;
            if (newSearch) {
                this.options = [];
            }
            this.busy = true;
            axios.get(newSearch ? this.dataUrl : url, {
                params: {
                    [`q_${this.valueKey}`.replace('.', '->')]: this.q
                }
            }).then(res => {
                this.toggleDropdown(true);
                if (newSearch) {
                    this.options = res.data.data;
                    this.$refs.options.scrollTop = 0;
                    this.focusedOption = 0;
                } else {
                    this.options = this.options.concat(res.data.data);
                }
                this.nextPage = res.data.next_page_url;
                this.busy = false;
            }, () => {
                this.busy = false;
            });
        },
        remove(value) {
            this.values = this.values.filter(x => x[this.key] !== value[this.key]);
            this.$emit('input', this.getValue());
        },
        select(value) {
            if (!this.dropdown) {
                return;
            }
            this.q = '';
            this.toggleDropdown(false);
            if (!this.multiple) {
                this.values = [value];
                this.$emit('input', this.getValue());
                this.$nextTick(() => {
                    this.$refs.select.focus();
                });
                return;
            }
            if (this.isDisabled(value)) {
                return this.remove(value);
            }
            this.values.push(value);
            this.$emit('input', this.getValue());
        },
        setValue(value) {
            if (!value) {
                return this.values = [];
            }
            this.values = Array.isArray(value) ? value : [value];
        },
        getValue() {
            if (this.multiple) {
                return this.values;
            }
            return this.values[0];
        },
        isDisabled(value) {
            return this.values.find(x => x[this.key] === value[this.key]);
        },
        outsideClick() {
            if (this.preventClosing) {
                return this.preventClosing = false;
            }
            this.dropdown = false;
        },
        pullValue(item, key, defaultVal = null) {
            // Key can have dot notation to pull nested elements
            let value = item;
            key.split('.').forEach(key => {
                if (!value) {
                    return defaultVal;
                }
                value = value[key];
            });
            return value;
        }
    },
    watch: {
        value: function(value) {
            if (typeof value === 'object' || value === undefined) {
                return this.setValue(value);
            }
            throw Error('Value in select3 shuld be object or undefined. Instead ' + typeof value + ' was given');
        },
        dropdown: function(visible) {
            if (!visible) {
                return;
            }
            this.focusedOption = 0;
            this.options = [];
            this.load();
            const pos = this.position();
            this.$refs.dropdown.style.left = pos.left + 'px';
            this.$refs.dropdown.style.top = pos.top + this.$el.offsetHeight + 'px';
            this.$refs.dropdown.style.width = Math.max(100, this.$el.offsetWidth) + 'px';
        }
    },
    mounted() {
        this.setValue(this.value);
        document.body.appendChild(this.$refs.dropdown);
        const options = this.$refs.options;
        options.onmousewheel = (e) => {
            options.scrollTop -= e.wheelDeltaY;
            if (options.scrollTop + options.offsetHeight > this.$refs.optionValues.offsetHeight - 20) {
                this.load(this.nextPage);
            }
            e.preventDefault();
        };
        this.clickHandler = this.outsideClick.bind(this);
        document.addEventListener('click', this.clickHandler, false);
    },
    beforeDestroy() {
        this.$refs.dropdown.remove();
    },
    destroyed() {
        document.removeEventListener('click', this.clickHandler);
    }
};
</script>



// WEBPACK FOOTER //
// resources/js/ui/Select3.vue